import { AbstractControl, ValidationErrors } from '@angular/forms';

// Phone numbers typed into the account forms are stored as E.164. A pasted number with its
// extension ("402-827-7220 x6720") used to be saved as one long number, so the forms now drop
// the extension on paste and only accept numbers of a possible length.

/** Remove a trailing extension ("x123", "ext. 123", "extension 123", "#123") from pasted text. */
export function stripPhoneExtension(text: string): string {
  return (text || '').replace(/\s*(?:ext(?:ension)?\.?|x|#)\s*\d+\s*$/i, '');
}

/** Add +1 to numbers typed without a country code. */
export function toE164(value: string | null | undefined): string {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.startsWith('+') ? trimmed : `+1${trimmed}`;
}

/**
 * True for a possible E.164 number: US/Canada must be +1 and a 10-digit number whose area code
 * doesn't start with 0 or 1; other countries 8-15 digits. Mirrors the API's libphonenumber check.
 */
export function isPossiblePhone(e164: string | null | undefined): boolean {
  const value = (e164 || '').trim();
  if (value.startsWith('+1')) {
    return /^\+1[2-9]\d{9}$/.test(value);
  }
  return /^\+[2-9]\d{7,14}$/.test(value);
}

/**
 * Form validator for an optional phone field: empty (or just "+1") passes, anything else must be
 * a possible number. Uses its own `possiblePhone` key because the shared text input strips
 * `invalidPhone` on every keystroke.
 */
export function possiblePhoneValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value || '').toString().trim();
  if (!value || value === '+1') {
    return null;
  }
  return isPossiblePhone(toE164(value)) ? null : { possiblePhone: true };
}
